// Utility functions for calculations and data processing

import { SEATS_PER_BUS, PRICE_PER_PASSENGER } from "../constants";

/**
 * Calculate total passengers for a single record
 */
export const calculateTotalPassengers = (record) => {
  const tickets = record.tickets || 0;
  const monthlyPass = record.monthlyPass || 0;
  const busStopPassengers =
    record.busStops?.reduce((sum, stop) => sum + (stop.passengers || 0), 0) ||
    0;

  return tickets + monthlyPass + busStopPassengers;
};

/**
 * Calculate AVR (Average passengers per Round/Trip)
 */
export const calculateAVR = (totalPassengers, totalTrips) => {
  if (totalTrips === 0) return 0;
  return totalPassengers / totalTrips;
};

/**
 * Calculate seat utilization percentage
 */
export const calculateSeatUtilization = (avr) => {
  return (avr / SEATS_PER_BUS) * 100;
};

/**
 * Calculate revenue based on passenger count
 */
export const calculateRevenue = (passengers) => {
  return passengers * PRICE_PER_PASSENGER;
};

/**
 * Group data by date
 */
export const groupByDate = (data) => {
  return data.reduce((acc, item) => {
    if (!acc[item.date]) {
      acc[item.date] = {
        totalPassengers: 0,
        totalTrips: 0,
        records: [],
      };
    }

    const totalPassengers = calculateTotalPassengers(item);
    acc[item.date].totalPassengers += totalPassengers;
    acc[item.date].totalTrips += item.tripCount || 1;
    acc[item.date].records.push(item);

    return acc;
  }, {});
};

/**
 * Group data by route
 */
export const groupByRoute = (data) => {
  return data.reduce((acc, item) => {
    if (!acc[item.route]) {
      acc[item.route] = {
        totalPassengers: 0,
        totalTrips: 0,
        records: [],
      };
    }

    const totalPassengers = calculateTotalPassengers(item);
    acc[item.route].totalPassengers += totalPassengers;
    acc[item.route].totalTrips += item.tripCount || 1;
    acc[item.route].records.push(item);

    return acc;
  }, {});
};

/**
 * Group data by day of week
 */
export const groupByDayOfWeek = (data) => {
  const weeklyData = {
    0: { total: 0, count: 0 }, // Sunday
    1: { total: 0, count: 0 }, // Monday
    2: { total: 0, count: 0 }, // Tuesday
    3: { total: 0, count: 0 }, // Wednesday
    4: { total: 0, count: 0 }, // Thursday
    5: { total: 0, count: 0 }, // Friday
    6: { total: 0, count: 0 }, // Saturday
  };

  data.forEach((item) => {
    const date = new Date(item.date);
    const dayOfWeek = date.getDay();
    const totalPassengers = calculateTotalPassengers(item);

    weeklyData[dayOfWeek].total += totalPassengers;
    weeklyData[dayOfWeek].count += 1;
  });

  return weeklyData;
};

/**
 * Calculate key metrics from all data
 */
export const calculateKeyMetrics = (data) => {
  if (!data || data.length === 0) {
    return {
      avgPassengersPerTrip: 0,
      avgSeatUtilization: 0,
      peakDay: null,
      peakDayPassengers: 0,
      topRoute: null,
      topRoutePassengers: 0,
    };
  }

  // Group by date
  const dailyData = groupByDate(data);
  const dates = Object.keys(dailyData);

  let totalAVR = 0;
  let totalSeatUtilization = 0;
  let peakDay = "";
  let peakDayPassengers = 0;

  dates.forEach((date) => {
    const dayData = dailyData[date];
    const avr = calculateAVR(dayData.totalPassengers, dayData.totalTrips);
    const seatUtilization = calculateSeatUtilization(avr);

    totalAVR += avr;
    totalSeatUtilization += seatUtilization;

    if (dayData.totalPassengers > peakDayPassengers) {
      peakDayPassengers = dayData.totalPassengers;
      peakDay = date;
    }
  });

  const avgAVR = dates.length > 0 ? totalAVR / dates.length : 0;
  const avgSeatUtil =
    dates.length > 0 ? totalSeatUtilization / dates.length : 0;

  // Find top route
  const routeData = groupByRoute(data);
  let topRoute = "";
  let topRoutePassengers = 0;

  Object.keys(routeData).forEach((route) => {
    if (routeData[route].totalPassengers > topRoutePassengers) {
      topRoutePassengers = routeData[route].totalPassengers;
      topRoute = route;
    }
  });

  return {
    avgPassengersPerTrip: avgAVR,
    avgSeatUtilization: avgSeatUtil,
    peakDay,
    peakDayPassengers,
    topRoute,
    topRoutePassengers,
  };
};

/**
 * Calculate summary statistics
 */
export const calculateSummary = (data) => {
  if (!data || data.length === 0) {
    return {
      totalTickets: 0,
      totalMonthlyPass: 0,
      totalBusStopPassengers: 0,
      totalTrips: 0,
      totalCustomers: 0,
      totalDaypassIncome: 0,
    };
  }

  const totalTickets = data.reduce((sum, item) => sum + (item.tickets || 0), 0);
  const totalMonthlyPass = data.reduce(
    (sum, item) => sum + (item.monthlyPass || 0),
    0,
  );
  const totalBusStopPassengers = data.reduce((sum, item) => {
    return (
      sum +
      (item.busStops?.reduce(
        (stopSum, stop) => stopSum + (stop.passengers || 0),
        0,
      ) || 0)
    );
  }, 0);
  const totalTrips = data.reduce((sum, item) => sum + (item.tripCount || 1), 0);
  const totalDaypassIncome = data.reduce(
    (sum, item) => sum + (item.daypass_income || 0),
    0,
  );
  const totalCustomers =
    totalTickets + totalMonthlyPass + totalBusStopPassengers;

  return {
    totalTickets,
    totalMonthlyPass,
    totalBusStopPassengers,
    totalTrips,
    totalCustomers,
    totalDaypassIncome,
  };
};

/**
 * Calculate performance metrics (seat utilization distribution)
 */
export const calculatePerformanceMetrics = (data) => {
  const dailyData = groupByDate(data);

  let highCount = 0;
  let mediumCount = 0;
  let lowCount = 0;

  Object.keys(dailyData).forEach((date) => {
    const dayData = dailyData[date];
    const avr = calculateAVR(dayData.totalPassengers, dayData.totalTrips);
    const seatUtilization = calculateSeatUtilization(avr);

    if (seatUtilization > 70) {
      highCount++;
    } else if (seatUtilization >= 40) {
      mediumCount++;
    } else {
      lowCount++;
    }
  });

  return {
    highUtilizationCount: highCount,
    mediumUtilizationCount: mediumCount,
    lowUtilizationCount: lowCount,
  };
};

/**
 * Calculate weekly statistics
 */
export const calculateWeeklyStats = (data) => {
  const weeklyData = groupByDayOfWeek(data);

  let weekdayTotal = 0;
  let weekdayCount = 0;
  let weekendTotal = 0;
  let weekendCount = 0;
  let maxPassengers = 0;
  let minPassengers = Infinity;
  let peakDay = "";
  let lowDay = "";

  Object.keys(weeklyData).forEach((dayIndex) => {
    const day = parseInt(dayIndex);
    const dayData = weeklyData[day];
    const average =
      dayData.count > 0 ? Math.round(dayData.total / dayData.count) : 0;

    // Skip days with no data
    if (average === 0) return;

    // Calculate weekday vs weekend averages
    if (day === 0 || day === 6) {
      // Weekend
      weekendTotal += average;
      weekendCount++;
    } else {
      // Weekday
      weekdayTotal += average;
      weekdayCount++;
    }

    // Find peak and low days
    if (average > maxPassengers) {
      maxPassengers = average;
      peakDay = day;
    }
    if (average < minPassengers) {
      minPassengers = average;
      lowDay = day;
    }
  });

  const weekdayAvg =
    weekdayCount > 0 ? Math.round(weekdayTotal / weekdayCount) : 0;
  const weekendAvg =
    weekendCount > 0 ? Math.round(weekendTotal / weekendCount) : 0;

  return {
    weekdayAvg,
    weekendAvg,
    peakDay,
    lowDay,
  };
};

/**
 * Get data for timeline charts
 */
export const getTimelineData = (data) => {
  const dailyData = groupByDate(data);
  const sortedDates = Object.keys(dailyData).sort(
    (a, b) => new Date(a) - new Date(b),
  );

  return sortedDates.map((date) => ({
    date,
    passengers: dailyData[date].totalPassengers,
    revenue: calculateRevenue(dailyData[date].totalPassengers),
    trips: dailyData[date].totalTrips,
  }));
};
